import { ReactElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { PersistPartial } from "redux-persist/es/persistReducer";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store";
import { initAccBoot } from "../../reducers/model/boot";
import { BootType, setShellConfig, setBootLoading } from "../../reducers/boot";
import { ShellConfigData } from "../../services/MyBizShell/ShellConfigDataLayer";
import { ANNUAL_INCENTIVES_PATHNAMES, FAA_SUB_ROUTES, ROUTE_PATHS } from "../../common/enums/routing-enums";
import TEALIUM from "../../common/enums/tealium-enums";
import { AUTH_ROUTE_PATHS } from "../../routing/AuthenticationRoutes";
import { isAuthOrErrorRoute, isErrorRoute } from "../../common/util/routeUtils";
import { createMenuConfiguration, getDefaultHomePage } from "../../common/util/Navigation/navigationUtil";
import { getPageTitleForRoute } from "../../common/util/Navigation/createNavigationConfig";
import { track } from "../../common/util/tealium";
import { ERROR_ROUTE_SUBPATHS, getAllErrorRoutes, getFullRoutePath } from "../../routing/ErrorRoutes";
import type { NavDropdown } from "../../common/interfaces/routing";
import { CUSTOMER_ROUTE_PATHS } from "../../routing/SubRoutes/Customers";
import { MBI_ROUTE_PATHS } from "../../routing/SubRoutes/MBI";
import { setNavigatedByPeriod, updatePeriod } from "../../reducers/period";
import { setNavigatedByBusiness } from "../../reducers/businessSelector";
import { hasSSIProgramActive } from "../../common/util/Navigation/checkReportEligibility";

import { ERROR_TYPE, ErrorPage } from "../ErrorPage/ErrorPage";
import {
  setAvailableRouteCheckComplete,
  setCurrentPageAvailable,
  setRedirectTo,
  setShowErrorPage,
} from "../../reducers/componentLoader";
import { FullPageComponentLoader } from "../../common/components/FullPageComponentLoader/FullPageComponentLoader";
import { HAT_SUB_ROUTES, GAR_SUB_ROUTES } from "../../routing/SubRoutes/Hat";
import { WHOS_NEW_SUB_ROUTES } from "../../routing/SubRoutes/WhosNewActionReport";
import { getPerformancePlusPath } from "../../common/util/Navigation/leaderGrowthDropdown";
import type { Config, LearnMore } from "../../common/interfaces/config";

const useInitAccBoot = ({ boot }: { boot: BootType & PersistPartial }) => {
  const [isPending, setIsPending] = useState(false);
  const bootPromise = useRef(false);

  useEffect(() => {
    if (bootPromise.current) {
      setIsPending(false);
      return;
    }
    const updateAccBoot = async () => {
      await initAccBoot(boot).then(() => {
        setIsPending(false);
        bootPromise.current = true;
      });
    };

    if (Object.keys(boot.configuration).length > 0) {
      updateAccBoot();
    }
  }, [boot]);

  return { isPending };
};

const navigationFlagGatedRoutes: ReadonlyArray<{ path: string; navigationFlag: string }> = [
  { path: ROUTE_PATHS.SOP_REPORT, navigationFlag: "showGroupSopTracking" },
];

const getRedirectUrl = ({
  pathname,
  availableRoutesForCurrentBusiness,
  configuration,
  selectedPeriod,
  isoCountryCode,
  navigatedByBusinessChange,
}: {
  pathname: string;
  availableRoutesForCurrentBusiness: string[];
  configuration: Config;
  selectedPeriod: string;
  isoCountryCode: string;
  navigatedByBusinessChange: boolean;
}): string | null => {
  const isNavigationFlagGatedRouteDisabled = navigationFlagGatedRoutes.some(
    ({ path, navigationFlag }) => pathname === path && !configuration.navigation?.[navigationFlag],
  );
  if (isNavigationFlagGatedRouteDisabled) {
    return getDefaultHomePage(configuration.cppModHomePage);
  }

  // Check for specific redirects first
  if (pathname === ROUTE_PATHS.PPE && availableRoutesForCurrentBusiness.includes(ROUTE_PATHS.PERFORMANCE_PLUS)) {
    return getPerformancePlusPath(configuration, selectedPeriod, isoCountryCode);
  }
  if (ROUTE_PATHS.PERFORMANCE_PLUS === pathname && availableRoutesForCurrentBusiness.includes(ROUTE_PATHS.PPE)) {
    return ROUTE_PATHS.PPE;
  }

  // Check if current path is available in business routes
  const isCurrentPathAvailable = availableRoutesForCurrentBusiness.some((route) =>
    matchPath({ path: route, end: true }, pathname),
  );

  // Only redirect to the monthly dashboard when the ABO landed on an unavailable route by
  // switching business (ABOT-13783). Direct URL navigation / period changes to a known but
  // inaccessible route must NOT redirect here - they fall through so the
  // INCENTIVE_UNAVAILABLE_FOR_PERIOD error page is shown instead (ABOT-12692).
  if (!isCurrentPathAvailable && navigatedByBusinessChange) {
    return ROUTE_PATHS.MONTHLY_PERFORMANCE;
  }

  return null; // No redirect needed
};

// Build the list of all known app routes from ROUTE_PATHS and sub-routes for 404 vs unavailable detection
const allKnownRoutes = [
  ...Object.values(ROUTE_PATHS),
  ...Object.values(AUTH_ROUTE_PATHS),
  ...getAllErrorRoutes(),
  ...FAA_SUB_ROUTES,
  ...HAT_SUB_ROUTES,
  ...GAR_SUB_ROUTES,
  ...CUSTOMER_ROUTE_PATHS,
  ...MBI_ROUTE_PATHS,
  ...WHOS_NEW_SUB_ROUTES,
] as string[];

const ShellConfigLoader = ({
  children,
  isComponentRoute,
  isSignedIn,
}: {
  children: ReactElement;
  isComponentRoute: boolean;
  isSignedIn: boolean;
}) => {
  const { t } = useTranslation(["navigation"]);
  const { selectedPeriod, selectedPerformanceYear, navigatedByPeriodChange } = useAppSelector((state) => state.period);
  const { user, configuration, isBootLoading, shellConfig } = useAppSelector((state) => state.boot);
  const { availableRouteCheckComplete, showErrorPage, isCurrentPageAvailable, redirectTo } = useAppSelector(
    (state) => state.componentLoader,
  );
  const { navigatedByBusinessChange } = useAppSelector((state) => state.businessSelectorData);
  const { pathname, hash } = useLocation();

  // Track if we've completed the route availability check for the current pathname
  // NOTE: We use local state (routeCheckDone + pageAvailable) alongside Redux state (showErrorPage)
  // because the Redux availableRouteCheckComplete flag has race conditions with other effects.
  // The Redux showErrorPage is still needed because ErrorPage.tsx uses it to reset state on button click.
  const [routeCheckDone, setRouteCheckDone] = useState(false);
  const [pageAvailable, setPageAvailable] = useState(true);

  const isAnnualPath = ANNUAL_INCENTIVES_PATHNAMES.includes(pathname) || hash === "#annual";
  const authRoutePaths = Object.values(AUTH_ROUTE_PATHS).map((route) => route.toString());
  const { data, isLoading: isShellConfigLoading } = ShellConfigData({ pathname });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const pageTitleConfig = getPageTitleForRoute(pathname, user.aff);
  const disclaimerSymbol = t("disclaimerSymbol", "+", { ns: "common" });
  const pageTitle = pageTitleConfig
    ? t(pageTitleConfig.key, pageTitleConfig.fallback, { symbol: disclaimerSymbol })
    : undefined;
  const learnMoreLink = pageTitleConfig?.learnMoreKey
    ? configuration.dashboardLinks?.learnMore?.[pageTitleConfig.learnMoreKey as keyof LearnMore]
    : undefined;

  // Create learn more tracking function based on route's Tealium category
  const learnMoreTealiumFunction = useCallback(() => {
    if (pageTitleConfig?.tealiumCategory) {
      track(
        TEALIUM.U_TAG_METHOD.LINK,
        TEALIUM.EVENT_NAME.CLICK_ACTION,
        pageTitleConfig.tealiumCategory,
        pageTitleConfig.tealiumCategory,
        "",
        "learn_more",
      );
    }
  }, [pageTitleConfig?.tealiumCategory]);

  // Reset error state when pathname, hash, or selectedPeriod changes to allow re-evaluation
  // This ensures that when user changes period or switches tabs (e.g., monthly <-> annual),
  // the page availability is rechecked
  useLayoutEffect(() => {
    dispatch(setShowErrorPage(false));
    setRouteCheckDone(false);
    setPageAvailable(true);
  }, [pathname, hash, selectedPeriod, dispatch]);

  // 1. once the shellConfig is loaded, create the menu config to populate dropdown menus on the header
  // Menu creation includes period-based incentive view logic (e.g., PPv2 for award 940 based on startPeriod and country)
  useLayoutEffect(() => {
    dispatch(setAvailableRouteCheckComplete(false));
    if (isAnnualPath) {
      dispatch(setBootLoading(true));
    }
    if (!isShellConfigLoading && data && Object.keys(configuration).length > 0) {
      dispatch(
        setShellConfig({
          ...data,
          menuConfig: createMenuConfiguration({ user, configuration }, data, selectedPeriod),
        }),
      );
      dispatch(setBootLoading(false));
    }
    dispatch(setRedirectTo(""));
  }, [isShellConfigLoading, data, user, isAnnualPath, dispatch, selectedPeriod, configuration]);

  // 2. after the menu config is created, check if the ABO is able to see the current page
  useEffect(() => {
    const ignoreRouteFromBusinessCheck = [
      ...authRoutePaths,
      ...getAllErrorRoutes(),
      ROUTE_PATHS.MONTHLY_PERFORMANCE,
      ROUTE_PATHS.Q_GROWTH_INCENTIVE,
      ROUTE_PATHS.EARLY_Q_BOOSTER,
      ROUTE_PATHS.LOG_OUT,
      ROUTE_PATHS.LEARN_ABOUT_CPP,
      ROUTE_PATHS.FEEDBACK,
    ];

    if (
      shellConfig.menuConfig &&
      shellConfig.menuConfig.length > 0 &&
      !isBootLoading &&
      !ignoreRouteFromBusinessCheck.includes(pathname)
    ) {
      const availableRoutesForCurrentBusiness = shellConfig.menuConfig
        .flatMap((dropdown: NavDropdown) => dropdown.items)
        .map((link) => link.path);

      // eslint-disable-next-line no-unsafe-optional-chaining
      const { reports = [] } = configuration?.actionReports;
      const { navigation } = configuration || {};
      const { showActionReports = false, showMBI = false } = navigation || {};

      if (showActionReports) {
        reports.forEach((report) => availableRoutesForCurrentBusiness.push(report.url));
        // Ensure the canonical renewals route is available even if the config still has the legacy .html URL
        if (!availableRoutesForCurrentBusiness.includes(ROUTE_PATHS.RENEWALS)) {
          availableRoutesForCurrentBusiness.push(ROUTE_PATHS.RENEWALS);
        }
        // FSI and Group SOP Action Reports are intentionally excluded from menuConfig/sidebar nav,
        // so they need their own config-gated allowlist entries here.
        if (navigation?.showFsiActionReport) {
          availableRoutesForCurrentBusiness.push(ROUTE_PATHS.FSI_ACTION_REPORT);
        }
        if (navigation?.showGroupSopTracking) {
          availableRoutesForCurrentBusiness.push(ROUTE_PATHS.SOP_REPORT);
        }
      }

      if (navigation?.showBronzeGroupTracking) {
        availableRoutesForCurrentBusiness.push(ROUTE_PATHS.BRONZE_GROUP_TRACKING);
      }

      // SSI Upline: Requires market config AND enrollment (matches backend Group Management API logic)
      if (hasSSIProgramActive(shellConfig.eligibleIncentives, navigation?.showSSIUpline ?? false)) {
        availableRoutesForCurrentBusiness.push(ROUTE_PATHS.SSI_UPLINE_REPORT);
      }

      if (showMBI) {
        availableRoutesForCurrentBusiness.push(...MBI_ROUTE_PATHS, ROUTE_PATHS.MBI);
      }

      const { showOnlineStatement = false } = navigation || {};
      if (showOnlineStatement) {
        availableRoutesForCurrentBusiness.push(ROUTE_PATHS.ONLINE_STATEMENTS);
      }

      const { showLeaderIncentive = false } = navigation || {};
      if (showLeaderIncentive) {
        availableRoutesForCurrentBusiness.push(ROUTE_PATHS.LEADER_INCENTIVE);
      }

      if (availableRoutesForCurrentBusiness.includes(ROUTE_PATHS.CUSTOMER_LIST)) {
        availableRoutesForCurrentBusiness.push(...CUSTOMER_ROUTE_PATHS, ROUTE_PATHS.CML, "/customer/overview");
      }

      if (availableRoutesForCurrentBusiness.includes(ROUTE_PATHS.HAT)) {
        availableRoutesForCurrentBusiness.push(...HAT_SUB_ROUTES);
      }

      if (shellConfig.hasGarLegAccess) {
        availableRoutesForCurrentBusiness.push(...GAR_SUB_ROUTES);
      }

      if (availableRoutesForCurrentBusiness.includes(ROUTE_PATHS.HAT)) {
        availableRoutesForCurrentBusiness.push(...GAR_SUB_ROUTES);
      }

      if (availableRoutesForCurrentBusiness.includes(ROUTE_PATHS.FAA)) {
        availableRoutesForCurrentBusiness.push(...FAA_SUB_ROUTES);
      }

      if (availableRoutesForCurrentBusiness.includes(ROUTE_PATHS.WHOS_NEW_ACTION_REPORT)) {
        availableRoutesForCurrentBusiness.push(...WHOS_NEW_SUB_ROUTES);
      }

      availableRoutesForCurrentBusiness.push(ROUTE_PATHS.GROUP_MANAGEMENT_DASHBOARD);
      // matchPath allows dynamic routes like /leg-qc-details/:abo or "/leg-qc-details/1234"
      const routeIsAvailable = availableRoutesForCurrentBusiness.some((route) =>
        matchPath({ path: route, end: true }, pathname),
      );

      const newPageAvailability = hash === "#annual" ? shellConfig.shouldShowAnnualPerformance : routeIsAvailable;

      // Only handle known routes here - unknown routes (404s) must skip both the redirect
      // and the page-availability update, so the catch-all * route can render the
      // PAGE_UNAVAILABLE error page. Otherwise redirectTo gets set for a 404 and the loader
      // shows indefinitely (ABOT-13823).
      const isKnownRoute = allKnownRoutes.some((route) => matchPath({ path: route, end: true }, pathname));

      if (isKnownRoute) {
        if (redirectTo === "") {
          const redirectURL = getRedirectUrl({
            pathname,
            availableRoutesForCurrentBusiness,
            configuration,
            isoCountryCode: user.isoCountryCode,
            selectedPeriod,
            navigatedByBusinessChange,
          });

          if (redirectURL) {
            dispatch(setRedirectTo(redirectURL));
          }
        }

        // Handle annual tab special case: redirect to monthly dashboard instead of showing error
        if (!newPageAvailability && hash === "#annual") {
          const lastPeriodOfPY = `${selectedPerformanceYear}08`;
          dispatch(updatePeriod({ period: lastPeriodOfPY }));
          dispatch(setNavigatedByPeriod(false));
          dispatch(setNavigatedByBusiness(false));
          navigate(ROUTE_PATHS.MONTHLY_PERFORMANCE);
          return;
        }

        setPageAvailable(newPageAvailability);
        setRouteCheckDone(true);

        // Still update Redux for other parts of the app that may depend on it
        if (isCurrentPageAvailable !== newPageAvailability) {
          dispatch(setCurrentPageAvailable(newPageAvailability));
        }
      }
    }
  }, [
    configuration,
    dispatch,
    isBootLoading,
    pathname,
    hash,
    shellConfig.menuConfig,
    shellConfig.eligibleIncentives,
    shellConfig.persona,
    shellConfig.hasGarLegAccess,
    authRoutePaths,
    shellConfig.shouldShowAnnualPerformance,
    isCurrentPageAvailable,
    navigatedByPeriodChange,
    navigatedByBusinessChange,
    user,
    selectedPeriod,
    redirectTo,
    selectedPerformanceYear,
    navigate,
  ]);

  // 3. if the page is not available,
  // check if redirect to url set then navigate to redirect url otherwise, show the error page
  useEffect(() => {
    if (availableRouteCheckComplete) {
      if (redirectTo !== "") {
        navigate(redirectTo);
        dispatch(setRedirectTo(""));
      } else if (!isCurrentPageAvailable) {
        // On the annual tab, navigate to monthly dashboard at the last period of the inaccessible PY
        if (hash === "#annual") {
          const lastPeriodOfPY = `${selectedPerformanceYear}08`;
          dispatch(updatePeriod({ period: lastPeriodOfPY }));
          // Reset flags before navigating so the effect doesn't re-fire on the new location
          dispatch(setAvailableRouteCheckComplete(false));
          dispatch(setNavigatedByPeriod(false));
          dispatch(setNavigatedByBusiness(false));
          navigate(ROUTE_PATHS.MONTHLY_PERFORMANCE);
          return;
        }
        // Show error page for all inaccessible routes (direct URL, business change, period change, etc.)
        dispatch(setShowErrorPage(true));
        return;
      }
      // reset flags
      dispatch(setAvailableRouteCheckComplete(false));
      dispatch(setNavigatedByPeriod(false));
      dispatch(setNavigatedByBusiness(false));
      dispatch(setRedirectTo(""));
    }
  }, [
    availableRouteCheckComplete,
    isCurrentPageAvailable,
    dispatch,
    navigate,
    redirectTo,
    hash,
    selectedPerformanceYear,
  ]);

  // Some routes (sign-in, sign-out, error pages, and the root path, which HomePage
  // itself routes based on sign-in state) must work while the user is signed out.
  const isExemptFromAuthCheck = isAuthOrErrorRoute(pathname) || pathname === ROUTE_PATHS.LOG_OUT || pathname === "/";
  const shouldShowNotAuthorized = !isExemptFromAuthCheck && !isSignedIn;

  // Route to the dedicated Not Authorized error page instead of rendering it inline,
  // so the URL reflects the error (consistent with how other auth failures navigate).
  // isBootLoading is persisted and only cleared once shell config loads, which never happens
  // signed out, so clear it here or the footer skeleton stays up on the error page.
  useEffect(() => {
    if (shouldShowNotAuthorized) {
      dispatch(setBootLoading(false));
      navigate(getFullRoutePath(ERROR_ROUTE_SUBPATHS.NOT_AUTHORIZED));
    }
  }, [shouldShowNotAuthorized, navigate, dispatch]);

  const isShellConfigReady = Boolean(shellConfig.aboInfo?.isoCountryCode);
  // Error routes (e.g. /error/notAuthorized) and the root path must render while signed out: isComponentRoute is
  // fixed at page load and the shell config query is skipped without a user, so neither loader clause can clear
  // there. At the root (where logout lands) HomePage has to mount to redirect a signed-out user.
  const skipsSessionLoader = isErrorRoute(pathname) || pathname === "/";
  const shouldShowLoader =
    (!skipsSessionLoader && isComponentRoute && (isBootLoading || !user.access_token || !isShellConfigReady)) ||
    (!skipsSessionLoader && isShellConfigLoading && !authRoutePaths.includes(pathname)) ||
    redirectTo ||
    shouldShowNotAuthorized;

  // Show error page if route check is done and page is not available
  const shouldShowErrorPage = routeCheckDone && (showErrorPage || (!pageAvailable && !redirectTo));

  return (
    <div data-testid="shell-config-loader-root">
      {shouldShowLoader ? (
        <FullPageComponentLoader />
      ) : (
        <>
          {shouldShowErrorPage ? (
            <ErrorPage
              errorType={ERROR_TYPE.INCENTIVE_UNAVAILABLE_FOR_PERIOD}
              pageTitle={pageTitle}
              learnMoreLink={learnMoreLink}
              learnMoreTealiumFunction={learnMoreTealiumFunction}
            />
          ) : (
            children
          )}
        </>
      )}
    </div>
  );
};

export const ComponentLoader = ({
  children,
  isComponentRoute,
  isSignedIn,
  period: _period,
  route: _route,
}: {
  children: ReactElement;
  isComponentRoute: boolean;
  isSignedIn: boolean;
  period: string;
  route: string;
}) => {
  const boot = useAppSelector(({ boot }) => boot);
  const { isPending } = useInitAccBoot({ boot });

  return (
    <ShellConfigLoader isComponentRoute={isComponentRoute} isSignedIn={isSignedIn}>
      {isPending && isComponentRoute ? <></> : <>{children}</>}
    </ShellConfigLoader>
  );
};
